import React, { useState } from 'react';
import cx from 'classnames';

import BookNewCoachingSessionSidebar from 'coaching/components/CoachingHomeView/BookNewCoachingSessionSidebar';
import CancelCoachingSessionInstanceSidebar from 'coaching/components/CoachingHomeView/CancelCoachingSessionInstanceSidebar';
import CoachingHomeFreeTrial from 'coaching/components/CoachingHomeView/CoachingHomeFreeTrial';
import Divider from 'components/Divider';
import Link from 'components/Link';
import MediaObject from 'components/MediaObject';
import RescheduleCoachingSessionInstanceSidebar from 'coaching/components/CoachingHomeView/RescheduleCoachingSessionInstanceSidebar';
import Row from 'components/Row';
import Text from 'components/Text';
import {
  CoachingSessionInstance,
  CoachingSessionTools,
  CoachMetadata,
  ParticipantMetadata,
} from 'coaching/components/CoachingHomeView/CoachingSessionsBySubjectContainer';
import {
  InstanceRowButtons,
  InstanceDescription,
  InstanceTextBadge,
} from 'coaching/components/CoachingHomeView/CoachingSessionInstanceRowComponents';
import { useCoachingViewState } from 'coaching/components/CoachingView';
// @ts-ignore
import userAgent from 'lib/currentUserAgent';

import 'coaching/components/styles/CoachingSessionUpcomingInstanceRows.scss';

export type Props = {
  instances: CoachingSessionInstance[];
  sessionTools?: CoachingSessionTools;
  recurringScheduleUuids: string[];
  subjectName: string;
  defaultCoach?: CoachMetadata;
  participantMetadata: ParticipantMetadata;
  userUuid: string;
  hasStudentInvitation: boolean;
};

export const DEFAULT_ROWS_TO_SHOW = 4;

export default function CoachingSessionUpcomingInstanceRows(props: Props) {
  const { instances, recurringScheduleUuids, defaultCoach } = props;
  const [showAllInstances, setShowAllInstances] = useState(false);
  const instancesToShow = showAllInstances ? instances : instances.slice(0, DEFAULT_ROWS_TO_SHOW);
  const shouldShowExpansionAction = instances.length >= DEFAULT_ROWS_TO_SHOW;
  const [selectedInstanceUuid, setSelectedInstanceUuid] = useState(instancesToShow[0].uuid);

  return (
    <>
      {instancesToShow.map((instance, index) => {
        const isLastItem = index === instances.length - 1;
        const shouldShowDivider = shouldShowExpansionAction || !isLastItem;
        return (
          <React.Fragment key={instance.uuid}>
            <CoachingSessionUpcomingInstanceRow
              {...props}
              instance={instance}
              coach={defaultCoach}
              isFirstItem={index === 0}
              isSelected={instance.uuid === selectedInstanceUuid}
              onSelectInstance={() => setSelectedInstanceUuid(instance.uuid)}
              userHasNoRecurringSchedules={recurringScheduleUuids.length === 0}
              instanceIsPartOfRecurringSchedule={recurringScheduleUuids.some(
                uuid => uuid === instance.coachingSessionScheduleUuid
              )}
            />
            {shouldShowDivider && (
              <Divider className="CoachingSessionUpcomingInstanceRows-divider" size="flush" />
            )}
          </React.Fragment>
        );
      })}
      {shouldShowExpansionAction && (
        <Link
          data-test="expansion-toggle"
          className="CoachingSessionUpcomingInstanceRows-expansionAction"
          color="chalkboard"
          onClick={() => setShowAllInstances(!showAllInstances)}>
          <Text weight="semibold" size="medium">
            View {showAllInstances ? 'fewer' : 'more'} sessions
          </Text>
        </Link>
      )}
    </>
  );
}

export type InstanceRowProps = {
  instance: CoachingSessionInstance;
  isSelected: boolean;
  isFirstItem: boolean;
  sessionTools?: CoachingSessionTools;
  instanceIsPartOfRecurringSchedule: boolean;
  userHasNoRecurringSchedules: boolean;
  onSelectInstance: () => void;
  subjectName: string;
  participantMetadata: ParticipantMetadata;
  coach?: CoachMetadata;
  userUuid: string;
  hasStudentInvitation: boolean;
};

export function CoachingSessionUpcomingInstanceRow(props: InstanceRowProps) {
  const { setSidebar, setSidebarIsOpen } = useCoachingViewState();
  const { sessionTools, coach, instance, isSelected } = props;

  const rowIsSelectable = !isSelected && !props.instance.isCancelled;
  const InstanceRowComponent = userAgent.isDesktop()
    ? CoachingSessionUpcomingInstanceRowInline
    : CoachingSessionUpcomingInstanceRowStacked;
  const handleEdit =
    sessionTools && coach
      ? () => {
          setSidebar(
            <RescheduleCoachingSessionInstanceSidebar
              originalInstanceUuid={props.instance.uuid}
              sessionTools={sessionTools}
              subjectName={props.subjectName}
              participantUuid={props.participantMetadata.uuid}
              coach={coach}
              onClose={() => setSidebarIsOpen(false)}
            />
          );
          setSidebarIsOpen(true);
        }
      : undefined;
  const handleCancel =
    sessionTools && coach
      ? () => {
          setSidebar(
            <CancelCoachingSessionInstanceSidebar
              sessionInstance={props.instance}
              onClose={() => setSidebarIsOpen(false)}
              onClickBookSession={() =>
                setSidebar(
                  <BookNewCoachingSessionSidebar
                    subjectName={props.subjectName}
                    sessionTools={sessionTools}
                    coach={coach}
                    onClose={() => setSidebarIsOpen(false)}
                    participantUuid={props.participantMetadata.uuid}
                  />
                )
              }
            />
          );
          setSidebarIsOpen(true);
        }
      : undefined;

  return (
    <div
      className={cx(
        'CoachingSessionUpcomingInstanceRow',
        rowIsSelectable && 'CoachingSessionUpcomingInstanceRow-hoverable'
      )}
      onClick={rowIsSelectable ? props.onSelectInstance : () => {}}>
      {instance.isFreeTrial && !instance.isCancelled ? (
        <>
          <Row size="small">
            <InstanceRowComponent
              {...props}
              onClickEdit={handleEdit}
              onClickCancel={handleCancel}
            />
          </Row>
          <CoachingHomeFreeTrial {...props} data-test="free-trial-row" />
        </>
      ) : (
        <InstanceRowComponent {...props} onClickEdit={handleEdit} onClickCancel={handleCancel} />
      )}
    </div>
  );
}

export type InstanceRowComponentProps = InstanceRowProps & {
  onClickEdit?: () => void;
  onClickCancel?: () => void;
};

export function CoachingSessionUpcomingInstanceRowInline(props: InstanceRowComponentProps) {
  const {
    instance,
    isSelected,
    isFirstItem,
    instanceIsPartOfRecurringSchedule,
    userHasNoRecurringSchedules,
  } = props;
  if (isFirstItem && instance.isCancelled) {
    // if this is the next upcoming instance but it's cancelled,
    // there's no need to show it
    return null;
  }
  const descriptionAndBadge = (
    <MediaObject center gutter="small" figure="right" inline>
      <InstanceDescription
        instance={instance}
        instanceIsPartOfRecurringSchedule={instanceIsPartOfRecurringSchedule}
        isSelected={isSelected}
      />
      <InstanceTextBadge
        isCancelled={instance.isCancelled}
        isPartOfRecurringSchedule={instanceIsPartOfRecurringSchedule}
        userHasNoRecurringSchedules={userHasNoRecurringSchedules}
      />
    </MediaObject>
  );

  if (!isSelected) {
    return descriptionAndBadge;
  }
  return (
    <MediaObject figure="right" center>
      {descriptionAndBadge}
      <InstanceRowButtons {...props} />
    </MediaObject>
  );
}

export function CoachingSessionUpcomingInstanceRowStacked(props: InstanceRowComponentProps) {
  const {
    instance,
    isSelected,
    isFirstItem,
    instanceIsPartOfRecurringSchedule,
    userHasNoRecurringSchedules,
  } = props;
  if (isFirstItem && instance.isCancelled) {
    // if this is the next upcoming instance but it's cancelled,
    // there's no need to show it
    return null;
  }

  const descriptionAndBadge = (
    <MediaObject figure="right">
      <InstanceDescription
        instance={instance}
        instanceIsPartOfRecurringSchedule={instanceIsPartOfRecurringSchedule}
        isSelected={isSelected}
      />
      <InstanceTextBadge
        isCancelled={instance.isCancelled}
        isPartOfRecurringSchedule={instanceIsPartOfRecurringSchedule}
        userHasNoRecurringSchedules={userHasNoRecurringSchedules}
      />
    </MediaObject>
  );

  if (!isSelected) {
    return descriptionAndBadge;
  }

  return (
    <>
      <Row size="xsmall">{descriptionAndBadge}</Row>
      <InstanceRowButtons {...props} />
    </>
  );
}
